import React, { useCallback, useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import {
  fetchMyHuayJackpotHistory,
  type HuayJackpotTicket,
} from '../../services/huayJackpotService'
import { HistoryCollapseCard, type HistoryDetailRow } from './HistoryCollapseCard'
import type { HistoryOutletContext } from './ModernHistoryLayout'

const PAGE_SIZE = 20

const formatNumber = (value: number | null | undefined, digits = 2) =>
  Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatBalls = (white?: number[], red?: number | null) => {
  const w = Array.isArray(white) && white.length ? white.join(', ') : '—'
  const r = red != null ? String(red) : '—'
  return `${w} + ${r}`
}

const statusLabel = (item: HuayJackpotTicket) => {
  const status = String(item.status || '').toLowerCase()
  if (item.isJackpot) return 'แจ็กพอต'
  if (Number(item.prizeAmount) > 0) return 'ถูกรางวัล'
  if (status === 'settled' || status === 'drawn') return 'เสร็จสิ้น'
  if (status === 'failed') return 'ล้มเหลว'
  if (status.includes('credit')) return 'เครดิตรางวัลไม่ชัดเจน'
  if (status.includes('debit')) return 'รอยืนยันการตัดเงิน'
  return item.status || '-'
}

const buildRows = (item: HuayJackpotTicket): HistoryDetailRow[] => {
  const prize = Number(item.prizeAmount || 0)
  const price = Number(item.ticketPrice || 0)
  const net = prize - price
  const rows: HistoryDetailRow[] = [
    { label: 'สถานะ', value: statusLabel(item) },
    { label: 'ราคาตั๋ว', value: `${formatNumber(price)} บาท` },
    { label: 'เลขที่เลือก', value: formatBalls(item.whiteNumbers, item.redNumber) },
    { label: 'ผลออกรางวัล', value: formatBalls(item.drawnWhite, item.drawnRed) },
    {
      label: 'รางวัล',
      value: `${formatNumber(prize)} บาท`,
      tone: prize > 0 ? 'positive' : 'default',
    },
    {
      label: 'สุทธิ',
      value: `${formatNumber(net)} บาท`,
      tone: net >= 0 ? 'positive' : 'negative',
    },
  ]
  if (item.tierLabel) rows.push({ label: 'ระดับรางวัล', value: item.tierLabel })
  if (item.whiteMatch != null) {
    rows.push({
      label: 'จับคู่',
      value: `${item.whiteMatch} ขาว${item.redMatch ? ' + แดง' : ''}`,
    })
  }
  rows.push({ label: 'วันที่ซื้อ', value: formatDateTime(item.purchasedAt || item.createdAt) })
  return rows
}

/** ประวัติหวยแจ็กพอต — อยู่กลุ่มประวัติเกมส์ที่ /history/games */
const HistoryHuayJackpotPage: React.FC = () => {
  useOutletContext<HistoryOutletContext>()
  const [items, setItems] = useState<HuayJackpotTicket[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadHistory = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetchMyHuayJackpotHistory({ page: pageNum, limit: PAGE_SIZE })
      const data = response.data
      setItems(data?.items || [])
      setPage(data?.page || pageNum)
      const total = Number(data?.total || 0)
      const limit = Number(data?.limit || PAGE_SIZE) || PAGE_SIZE
      setTotalPages(Math.max(1, Math.ceil(total / limit) || 1))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'โหลดประวัติไม่สำเร็จ')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory(1)
  }, [loadHistory])

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore
    }
  }

  return (
    <div className="htd-hist__stack">
      <h2 className="htd-hist__page-title">ประวัติหวยแจ็กพอต</h2>

      <div className="htd-hist__panel">
        <div className="htd-hist__summary">
          <div>
            <strong>ตั๋วหวยแจ็กพอตของคุณ</strong>
            <span>เลขเลือก · ผลออกรางวัล · รางวัล</span>
          </div>
          <div className="text-end">
            <Link to="/points/games/huay-jackpot" className="htd-hist__inline-link">
              ไปเล่นหวยแจ็กพอต
            </Link>
          </div>
        </div>
      </div>

      <div className="htd-hist__list">
        {loading && (
          <div className="htd-hist__loading">
            <h4>
              <i className="fas fa-spinner fa-spin me-2" />
              กำลังโหลดประวัติหวยแจ็กพอต...
            </h4>
          </div>
        )}

        {!loading && error && (
          <div className="htd-hist__shell">
            <h4>{error}</h4>
            <button type="button" className="htd-hist__retry-btn" onClick={() => void loadHistory(page)}>
              ลองโหลดอีกครั้ง
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="htd-hist__empty">
            <h4>ยังไม่มีประวัติหวยแจ็กพอต</h4>
            <p>เมื่อซื้อตั๋ว รายการจะแสดงที่นี่</p>
            <Link to="/points/games/huay-jackpot" className="htd-hist__inline-link">
              ไปเล่นหวยแจ็กพอต
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="htd-hist__panel mb-2">
              <div className="htd-hist__summary">
                <div>
                  <strong>
                    พบ {items.length} รายการ
                    {totalPages > 1 ? ` (หน้า ${page}/${totalPages})` : ''}
                  </strong>
                  <span>แตะการ์ดเพื่อดูรายละเอียด</span>
                </div>
              </div>
            </div>

            {items.map((item) => {
              const prize = Number(item.prizeAmount || 0)
              const price = Number(item.ticketPrice || 0)
              const leftIcon = item.isJackpot || prize > 0 ? 'success' : 'reject'
              return (
                <HistoryCollapseCard
                  key={item.id}
                  id={item.id}
                  leftIcon={leftIcon}
                  refId={item.id}
                  onCopyRef={() => void handleCopyRef(item.id)}
                  rows={buildRows(item)}
                  header={(
                    <div className="w-100 pe-md-4">
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
                        หวยแจ็กพอต ·{' '}
                        {item.isJackpot
                          ? `แจ็กพอต ${formatNumber(prize)} บาท`
                          : `รางวัล ${formatNumber(prize)} บาท`}
                      </div>
                      <small style={{ color: 'rgba(254, 243, 199, 0.85)', display: 'block' }}>
                        ราคา {formatNumber(price)} บาท · {statusLabel(item)} ·{' '}
                        {formatDateTime(item.purchasedAt || item.createdAt)}
                      </small>
                    </div>
                  )}
                />
              )
            })}

            {totalPages > 1 && (
              <div className="d-flex justify-content-center gap-2 mt-2">
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page <= 1}
                  onClick={() => void loadHistory(page - 1)}
                >
                  ก่อนหน้า
                </button>
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page >= totalPages}
                  onClick={() => void loadHistory(page + 1)}
                >
                  ถัดไป
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default HistoryHuayJackpotPage
